import { useEffect, useRef, useState } from 'react';
import { dayLabel, drawLayout, drawViewport } from './drawModel';
import DrawWheel, { useWheelMoment } from './DrawWheel';

export function DrawGroup({ index, slots, last, selected, onSlot }) {
  return <section className={`draw-group draw-group--${index}`} aria-label={`Группа ${index + 1}`}>
    <header><span>ГРУППА</span><strong>{index + 1}</strong><small>{slots.filter(Boolean).length} / {slots.length}</small></header>
    <ol>{slots.map((name, slot) => <li key={slot} className={`${name ? 'is-filled' : 'is-empty'}${last?.group === index && last?.slot === slot ? ' is-latest' : ''}`}>
      <span className="draw-seed">{String(slot + 1).padStart(2, '0')}</span>
      {onSlot ? <button className={selected?.[0] === index && selected?.[1] === slot ? 'is-selected' : ''} onClick={() => onSlot(index, slot)}>{name || 'Выбрать место'}</button>
        : <strong key={name || 'empty'} title={name || undefined}>{name || <span>Ожидает команду<i /></span>}</strong>}
      {name && <span className="draw-team-mark" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>}
    </li>)}</ol>
    <footer><span>DOUBLE ELIMINATION</span><span>2 КОМАНДЫ В ПЛЕЙ-ОФФ ↗</span></footer>
  </section>;
}

export function ScheduleBoard({ matches, tour, compact = false }) {
  return <section className={`draw-schedule${compact ? ' is-compact' : ''}`} style={{ '--schedule-rows': Math.ceil(matches.length / 2) }} aria-label="Расписание групповой стадии">
    <header><div><span className="draw-kicker">ГРУППОВАЯ СТАДИЯ</span><h2>РАСПИСАНИЕ</h2></div><span className="draw-timezone">UTC+5<br />2 ПЛОЩАДКИ</span></header>
    <div className="draw-schedule-head"><span>ВРЕМЯ</span><span>ГР.</span><span>ПАРА / СТАДИЯ</span><span>ПК</span></div>
    <div className="draw-schedule-rows">{matches.map((match) => <div className="draw-match" key={match.id}>
      <time><b>{match.time}</b><small>{dayLabel(tour.startDate, match.day)}</small></time>
      <span className="draw-match-group">{match.group}</span>
      <div className="draw-match-pair"><span title={match.teamA}>{match.teamA}</span><i>×</i><span title={match.teamB}>{match.teamB}</span></div>
      <span className="draw-stream">{match.stream}</span>
    </div>)}</div>
    <footer>В1 / В2 — первые матчи · ВФ — верхний финал · Н1 — нижняя сетка<br />Плей-офф: расписание будет опубликовано отдельно.</footer>
  </section>;
}

function BrandScene({ tour, notice }) {
  return <section className="draw-brand-scene">
    <div className="draw-brand-orbit" aria-hidden="true" />
    <span className="draw-kicker">СЕВЕРО-КАЗАХСТАНСКАЯ ОБЛАСТЬ</span>
    <img src="/assets/logo-federation.png" alt="OO EkoTime проект Федерация Киберспорта СКО" />
    <h1><span className="draw-brand-project">OO EkoTime <small>проект</small></span>{' '}Федерация<br />{' '}Киберспорта <em>СКО</em></h1>
    <div className="draw-brand-rule" />
    <p>{tour.title}</p><span>{notice || 'ПЕТРОПАВЛОВСК · NOVA GAMING ARENA'}</span>
  </section>;
}

export default function DrawStage({ data, online, matches, search, clock, spinControl, onLayout }) {
  const host = useRef(null);
  const [box, setBox] = useState({ width: 1920, height: 1080 });
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => setBox({ width: entry.contentRect.width, height: entry.contentRect.height }));
    if (host.current) observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  const layout = drawLayout(search, box.width, box.height);
  const scene = layout.scene || data.draw.scene || 'combined';
  const moment = useWheelMoment(data.draw.wheel, clock);
  const showWheel = scene === 'wheel' || (scene === 'combined' && (data.draw.phase === 'drawing' || moment.active));
  const viewport = drawViewport(layout, box.width, box.height);
  useEffect(() => { onLayout?.(layout); }, [onLayout, layout.screen, layout.square]); // eslint-disable-line react-hooks/exhaustive-deps
  const count = data.draw.slots.flat().filter(Boolean).length;
  const phase = { ready: 'ОЖИДАЕМ НАЧАЛО', drawing: 'ЖЕРЕБЬЁВКА ИДЁТ', complete: 'ГРУППЫ СФОРМИРОВАНЫ' }[data.draw.phase];
  return <div className="draw-stage-host" ref={host}>
    <div className="draw-stage-window" style={{ width: box.width, height: box.height }}>
      <div className={`draw-stage ${layout.square ? 'is-square' : 'is-wide'} scene-${showWheel ? 'wheel' : scene}${scene === 'wheel' ? ' is-wheel-only' : ''}${layout.calibration ? ' is-calibration' : ''}`}
        style={{ width: viewport.width, height: viewport.height, transform: `scale(${viewport.scale})`, left: viewport.offset }}>
        <div className="draw-stage-texture" aria-hidden="true" />
        {scene === 'brand' ? <BrandScene tour={data.tour} notice={data.draw.notice} /> : <>
          <header className="draw-stage-head">
            <div className="draw-stage-identity"><img src="/assets/logo-federation.png" alt="OO EkoTime проект Федерация Киберспорта СКО" /><div><span className="draw-kicker">NOVA DOTA 2 OPEN · СКО</span><h1>ЖЕРЕБЬЁВКА<span>.</span></h1></div></div>
            <div className="draw-stage-event"><b>{data.tour.date_ru}</b><span>ПЕТРОПАВЛОВСК · NOVA GAMING ARENA</span></div>
            <div className="draw-stage-progress"><span className={online ? 'is-online' : 'is-offline'}><i />{online ? phase : 'СВЯЗЬ ПРЕРВАНА · ПОВТОРЯЕМ'}</span><strong>{String(count).padStart(2, '0')}<small> / {data.draw.pool.length || data.draw.slots.flat().length}</small></strong></div>
          </header>
          <div className={`draw-stage-body${showWheel ? ' draw-wheel-layout' : ''}`}>
            {showWheel ? <>
              {scene !== 'wheel' && data.draw.slots.map((slots, i) => <DrawGroup key={i} index={i} slots={slots} last={data.draw.last} />)}
              <DrawWheel draw={data.draw} moment={moment} clock={clock} online={online} spinControl={spinControl} />
            </> : <>
              {scene !== 'schedule' && <div className="draw-groups">{data.draw.slots.map((slots, i) => <DrawGroup key={i} index={i} slots={slots} last={data.draw.last} />)}</div>}
              {scene !== 'groups' && <ScheduleBoard matches={matches} tour={data.tour} compact={scene === 'combined'} />}
            </>}
          </div>
          <footer className="draw-stage-footer"><span>{data.draw.notice || (data.draw.last ? `ПОСЛЕДНЯЯ КОМАНДА: ${data.draw.last.team} → ГРУППА ${data.draw.last.group + 1}` : '4 ГРУППЫ · 2 КОМАНДЫ ИЗ КАЖДОЙ В ПЛЕЙ-ОФФ')}</span><b>{data.tour.prize_ru || 'NOVA DOTA 2 OPEN'}</b><span>CYBERSPORTLEAGUE.KZ</span></footer>
        </>}
        {!online && scene === 'brand' && <span className="draw-brand-offline">Нет связи · ожидаем обновление</span>}
        {layout.calibration && <div className="draw-calibration" aria-label="Сетка калибровки"><i /><i /><span>SAFE AREA · {Math.round(viewport.width)} × {Math.round(viewport.height)} · PANEL {layout.panel}</span></div>}
      </div>
    </div>
  </div>;
}
